import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Button, Chip, Input } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import CityPicker from '../../components/CityPicker';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getCurrentPosition, requestLocationPermission } from '../../services/location';

const LABELS = [
  { key: 'home', label: '🏠 Home' },
  { key: 'work', label: '🏢 Work' },
  { key: 'other', label: '📍 Other' },
];

const AddAddressPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const existing = state && state.address;
  const returnTo = state && state.returnTo;

  const { location } = useApp();
  const { user } = useAuth();
  const toast = useToast();

  const [form, setForm] = useState({
    label: (existing && existing.label) || 'home',
    name: (existing && existing.name) || (user && user.name) || '',
    phone: (existing && existing.phone) || (user && user.phone) || '',
    line1: (existing && existing.line1) || '',
    line2: (existing && existing.line2) || '',
    landmark: (existing && existing.landmark) || '',
    city: (existing && existing.city) || location.city || '',
    district: (existing && existing.district) || '',
    state: (existing && existing.state) || '',
    pincode: (existing && existing.pincode) || '',
    isDefault: Boolean(existing && existing.isDefault),
    // Saved for someone else: their name and phone become the contact the shop calls.
    forOther: Boolean(existing && existing.forOther),
    latitude: existing && existing.location && existing.location.coordinates ? existing.location.coordinates[1] : null,
    longitude: existing && existing.location && existing.location.coordinates ? existing.location.coordinates[0] : null,
  });
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  /** GPS + reverse geocode: fills every field, all of which stay editable. */
  const useCurrent = async () => {
    setLocating(true);
    try {
      const allowed = await requestLocationPermission();
      if (!allowed) { setError('Allow location access to use your current position'); return; }
      const fix = await getCurrentPosition({ fresh: true });
      if (!fix || !fix.coordinates) { setError('Could not get your position'); return; }
      const [longitude, latitude] = fix.coordinates;
      let a = {};
      try { a = (await api.maps.reverse({ lat: latitude, lng: longitude })).data || {}; } catch (e) { /* keep the point */ }
      setForm((f) => ({
        ...f, latitude, longitude,
        line1: a.address || f.line1, city: a.city || f.city, district: a.district || f.district, state: a.state || f.state, pincode: a.pincode || f.pincode,
      }));
      setError('');
    } catch (e) { setError('Could not get your position'); } finally { setLocating(false); }
  };

  const setWho = (forOther) => setForm((f) => ({
    ...f,
    forOther,
    name: forOther ? (f.forOther ? f.name : '') : ((user && user.name) || ''),
    phone: forOther ? (f.forOther ? f.phone : '') : ((user && user.phone) || ''),
  }));
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    if (form.line1.trim().length < 3) { setError('Enter the house / street address'); return; }
    if (form.forOther && (!form.name.trim() || form.phone.length < 10)) { setError('Enter the name and phone of the person this is for'); return; }
    setSaving(true);
    try {
      const existingCoords = existing && existing.location && existing.location.coordinates;
      const payload = {
        ...form,
        latitude: form.latitude != null ? form.latitude : (existingCoords ? existingCoords[1] : location.latitude),
        longitude: form.longitude != null ? form.longitude : (existingCoords ? existingCoords[0] : location.longitude),
      };
      if (existing) await api.addresses.update(existing._id, payload);
      else await api.addresses.create(payload);
      toast.success(existing ? 'Address updated.' : 'Address saved.');
      navigate(returnTo || '/addresses', { replace: true });
    } catch (err) {
      toast.error(err.message, 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 620 }}>
      <PageHeader
        title={existing ? 'Edit address' : 'Add address'}
        subtitle="Where should we deliver or serve?"
      />

      <Reveal className="card sh-sm">
        <form onSubmit={save}>
          <button type="button" className="card press" onClick={useCurrent} disabled={locating}
            style={{ width: '100%', textAlign: 'left', display: 'flex', gap: 12, alignItems: 'center', background: 'var(--primary-light)', borderColor: 'var(--primary-soft)', marginBottom: 16 }}>
            <span style={{ fontSize: 18 }}>📍</span>
            <span>
              <div className="t-title" style={{ color: 'var(--primary)' }}>{locating ? 'Finding you…' : 'Use my current location'}</div>
              <div className="t-tiny">Fills in the address below - you can edit anything.</div>
            </span>
          </button>

          <label className="field-label">Who is this for?</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
            <Chip label="🙋 Me" active={!form.forOther} onClick={() => setWho(false)} />
            <Chip label="👥 Someone else" active={form.forOther} onClick={() => setWho(true)} />
          </div>
          {form.forOther ? <div className="t-tiny" style={{ marginBottom: 12 }}>Their name and number go on the order, and the shop or provider will call them.</div> : null}

          <label className="field-label">Save this address as</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 18 }}>
            {LABELS.map((l) => (
              <Chip
                key={l.key}
                label={l.label}
                active={form.label === l.key}
                onClick={() => setForm({ ...form, label: l.key })}
              />
            ))}
          </div>

          <div className="form-row">
            <Input
              label={form.forOther ? 'Their name' : 'Receiver name'}
              icon="👤"
              className="grow"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Input
              label={form.forOther ? 'Their phone number' : 'Phone number'}
              icon="📞"
              className="grow"
              inputMode="numeric"
              maxLength={10}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
            />
          </div>

          <Input
            label="House / flat / street"
            icon="🏠"
            placeholder="H-42, Rani Bagh"
            value={form.line1}
            onChange={(e) => { setForm({ ...form, line1: e.target.value }); setError(''); }}
            error={error}
          />
          <Input
            label="Area / colony (optional)"
            value={form.line2}
            onChange={(e) => setForm({ ...form, line2: e.target.value })}
          />
          <Input
            label="Landmark (optional)"
            placeholder="Near…"
            value={form.landmark}
            onChange={(e) => setForm({ ...form, landmark: e.target.value })}
          />

          <CityPicker
            label="City"
            value={form}
            onChange={({ city, district, state: st }) => setForm({ ...form, city, district, state: st })}
          />

          <Input
            label="Pincode"
            inputMode="numeric"
            maxLength={6}
            value={form.pincode}
            onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '') })}
          />

          <Chip
            label={form.isDefault ? '✓ Set as default address' : 'Set as default address'}
            active={form.isDefault}
            onClick={() => setForm({ ...form, isDefault: !form.isDefault })}
          />

          <Button
            title={existing ? 'Update address' : 'Save address'}
            type="submit"
            loading={saving}
            size="lg"
            full
            style={{ marginTop: 20 }}
          />
        </form>
      </Reveal>
    </div>
  );
};

export default AddAddressPage;
